import { ReactNode, CSSProperties, ImgHTMLAttributes, HTMLAttributes, createRef, forwardRef } from 'react';
import _ from "lodash";
import { eIconType, SettingsType, ItemsMgmtDialogInfo } from '@Primitive/SettingsListType';
import { Dropdown, Whisper, Popover } from 'rsuite';
import { isDefined, IsNullOrWhiteSpace, getRandomSmallKey, IsNullOrEmpty } from '@Utils/Micro';
import { popupStaticWindow } from '@Utils/Utils';
import { IAdvancedTippy, AdvancedTippy } from '@Primitive/AdvancedTippy';
import "./ToolbarIconButton.less";
// ================================================================================================
export type TooltipPosition = "top" | "bottom" | "left" | "right";
export interface ToolbarIconButtonProps extends ImgHTMLAttributes<HTMLElement> {
    tooltip?: string;
    tooltipPosition?: TooltipPosition;
    showBubble?: boolean;
    bubbleContent?: string;
    bubbleBackgroundColor?: string;
    bubbleColor?: string;
    bubbleFontSize?: number;
    bubbleOtherStyles?: any;
    iconType?: eIconType;
    selected?: boolean;
    onIconClickDropdown?: boolean;
    inMenuList?: boolean;
    advancedPopup?: IAdvancedTippy;
    advancedTooltip?: IAdvancedTippy;
    buttonPressed?: (element: any, data: any) => void;
    elementChanged?: (dataPath: string, data: any, prevData: any, formData: any) => void;
    isHighlighted?: boolean;
}
// ------------------------------------------------------------------------------------------------
export const ToolbarIconButton = forwardRef<any, ToolbarIconButtonProps>(function (allProps: ToolbarIconButtonProps, ref) {
    let {
        iconType,
        tooltip,
        tooltipPosition,
        selected,
        className,
        showBubble,
        bubbleBackgroundColor,
        bubbleContent,
        bubbleColor,
        bubbleFontSize,
        bubbleOtherStyles,
        advancedTooltip,
        advancedPopup,
        buttonPressed,
        elementChanged,
        inMenuList,
        isHighlighted,
        ...props
    } = allProps;

    let bubbleJSX = (showBubble && !IsNullOrEmpty(bubbleContent)) ? <span className='toolbar-icon-button-bubble' style={{
        backgroundColor: bubbleBackgroundColor,
        color: bubbleColor,
        fontSize: bubbleFontSize,
        ...bubbleOtherStyles
    }}>
        {bubbleContent}
    </span> : null;

    // set custom class if selected
    if (selected) {
        if (!isDefined(className))
            className = 'selected';
        else
            className += " selected";
    }

    if (isHighlighted ?? true)
        className += ' highlighted';

    const jsx = !isDefined(iconType) || iconType == eIconType.fontAwesome
        ? <div style={{ position: inMenuList ? 'unset' : 'relative' }} ref={ref}>
            {bubbleJSX}
            {isDefined(className)
                ? <i id="icons" className={"toolbar-icon-button " + className} {...props} />
                : <div id="icons" {...props} />}
        </div>
        : <div
            id="icons"
            style={{ position: inMenuList ? 'unset' : 'relative' }}
            className={"toolbar-icon-button " + className}
            onClick={props.onClick} ref={ref}
        >
            {bubbleJSX}
            <img className='toolbar-icon-button-img' {...props} onClick={undefined} />
        </div>;

    if (IsNullOrWhiteSpace(tooltip)
        && !isDefined(advancedTooltip)
        && !isDefined(advancedPopup))
        return jsx;

    let placement: TooltipPosition = "bottom";
    if (isDefined(tooltipPosition))
        placement = tooltipPosition;

    return (
        <AdvancedTippy
            placement={placement}
            tooltip={tooltip}
            advancedTooltip={advancedTooltip}
            advancedPopup={advancedPopup}
            iconClicked={props.onClick}
            buttonPressed={buttonPressed}
            elementChanged={elementChanged}
            ref={ref}
        >
            {jsx}
        </AdvancedTippy>
    );
})
// ------------------------------------------------------------------------------------------------
export interface ToolbarIconButtonSeparatorProps extends HTMLAttributes<HTMLElement> { }
export function ToolbarIconButtonSeparator(props: ToolbarIconButtonSeparatorProps) {
    return <span id="separators" {...props} />;
}
// ================================================================================================
/**
 * Information about toolbar icons.
 */
export interface ToolbarIconInfo {
    /**
     * Define button type
     */
    type?: 'text' | 'icon' | 'image';

    /**
     * If true, all other fields are ignored
     */
    isSeparator?: boolean;

    /**
     * Support text based toolbars icons
     */
    label?: string;

    tooltip?: string;
    id?: string;
    icon?: string;
    iconType?: eIconType;

    /**
     * Optional color when icon type is font-awesome.
     */
    iconColor?: string;

    /**
     * Enable Bubble
     */
    showBubble?: boolean;
    bubbleBackgroundColor?: string;
    bubbleColor?: string;
    bubbleFontSize?: number;
    bubbleContent?: string;

    /**
     * Whether to set as selected upon user click or just send the port event.
     */
    autoSelect?: boolean;

    /**
     * nested toolbar icons.
     */
    nestedItems?: ToolbarIconInfo[];
    nestedItemsPlacement?:
    | 'bottomStart'
    | 'bottomEnd'
    | 'topStart'
    | 'topEnd'
    | 'leftStart'
    | 'rightStart'
    | 'leftEnd'
    | 'rightEnd'

    /**
     * right Icon (mostly used for nested menu)
     */
    rightIcon?: string;

    /**
    *  on Icon click show also dropdown
    */
    onIconClickDropdown?: boolean;

    /**
     * Default is true
     */
    isClickable?: boolean;

    /**
     * Styles for buttons/strings/icons
     */
    style?: CSSProperties;

    /**
     * Set external page url
     */
    externalPageUrl?: string;

    // dynamic form inside tooltip/popup
    advancedTooltip?: IAdvancedTippy;
    advancedPopup?: IAdvancedTippy;

    // extends with priv data
    priv?: any;

    isHighlighted?: boolean;
}
// --------------------------------------------------------------------------------------------
export function ToolbarIconInfo_GetSettingList(hideAutoSelectable: boolean = false, enableTableDropDown: boolean = false): ItemsMgmtDialogInfo<any, ToolbarIconInfo>[] {
    return _.compact([
        (enableTableDropDown) ? null :
            {
                type: SettingsType.Boolean,
                label: "Separator",
                dataPath: "isSeparator"
            },
        (enableTableDropDown) ? null :
            (hideAutoSelectable) ?
                {
                    type: SettingsType.Boolean,
                    label: "Auto selectable",
                    dataPath: "autoSelect",
                    isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
                } : null,
        {
            type: SettingsType.String,
            label: "ID",
            dataPath: "id",
            isUnique: true,
            isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
        },
        {
            type: SettingsType.DropDown,
            label: "Type",
            dataPath: "type",
            items: [
                { label: "Text", value: "text" },
                { label: "Icon", value: "icon" }
            ]
        },
        {
            type: SettingsType.String,
            label: "Label",
            dataPath: "label",
            isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
        },
        (enableTableDropDown) ? null :
            {
                type: SettingsType.String,
                label: "Tooltip",
                dataPath: "tooltip",
                isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
            },
        (enableTableDropDown) ? null :
            {
                type: SettingsType.Boolean,
                label: "Is Dropdown",
                dataPath: "onIconClickDropdown"
            },
        {
            type: SettingsType.ExtendedIcon,
            label: "Icon",
            dataPath: "icon",
            iconTypePath: "iconType",
            supportUpload: { ext: 'png', imgQuality: 0.9, maxHeight: 64, maxWidth: 64 },
            supportUrl: true,
            isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
        },
        {
            type: SettingsType.ColorPicker,
            label: "Icon Color",
            dataPath: "iconColor",
            isEnabled: (data, row: ToolbarIconInfo) => row.iconType == eIconType.fontAwesome
        },
        (enableTableDropDown) ? null :
            {
                type: SettingsType.Boolean,
                label: 'Has Bubble',
                dataPath: 'showBubble',
                tooltip: 'If enabled, show the bubble indicator'
            },
        (enableTableDropDown) ? null :
            {
                type: SettingsType.ColorPicker,
                label: 'Bubble Text Color',
                dataPath: 'bubbleColor',
                isEnabled: (data, row: ToolbarIconInfo) => row.showBubble
            },
        (enableTableDropDown) ? null :

            {
                type: SettingsType.ColorPicker,
                label: 'Bubble Bg Color',
                dataPath: 'bubbleBackgroundColor',
                isEnabled: (data, row: ToolbarIconInfo) => row.showBubble
            },
        (enableTableDropDown) ? null :
            {
                type: SettingsType.String,
                label: "Bubble Content",
                dataPath: "bubbleContent",
                isEnabled: (data, row: ToolbarIconInfo) => row.showBubble
            },
        (enableTableDropDown) ? null :
            {
                type: SettingsType.Slider,
                label: 'Bubble Font Size',
                dataPath: 'bubbleFontSize',
                minValue: 9,
                maxValue: 12,
                stepValue: 1,
                valuePosfix: 'px',
                isEnabled: (data, row: ToolbarIconInfo) => row.showBubble

            },
        (enableTableDropDown) ? null :
            {
                type: SettingsType.ItemsMgmtDialog,
                label: "Nested Buttons",
                dataPath: "nestedItems",
                isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator,
                itemsInfo: _.compact([
                    {
                        type: SettingsType.Boolean,
                        label: "Separator",
                        dataPath: "isSeparator"
                    },
                    (hideAutoSelectable) ?
                        {
                            type: SettingsType.Boolean,
                            label: "Auto selectable",
                            dataPath: "autoSelect",
                            isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
                        } : null,
                    {
                        type: SettingsType.String,
                        label: "ID",
                        dataPath: "id",
                        isUnique: true,
                        isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
                    },
                    {
                        type: SettingsType.String,
                        label: "Label",
                        dataPath: "label",
                        isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
                    },
                    {
                        type: SettingsType.String,
                        label: "Tooltip",
                        dataPath: "tooltip",
                        isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
                    },
                    {
                        type: SettingsType.ExtendedIcon,
                        label: "Icon",
                        dataPath: "icon",
                        iconTypePath: "iconType",
                        supportUpload: { ext: 'png', imgQuality: 0.9, maxHeight: 64, maxWidth: 64 },
                        supportUrl: true,
                        isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: "Icon Color",
                        dataPath: "iconColor",
                        isEnabled: (data, row: ToolbarIconInfo) => row.iconType == eIconType.fontAwesome
                    }
                ]),
                createNewItem: () => {
                    return {
                        icon: "",
                        tooltip: "Icon tooltip",
                        onIconClickDropdown: false,
                        iconColor: "gray",
                        iconType: eIconType.fontAwesome,
                        showBubble: false,
                        bubbleBackgroundColor: '#D0021B',
                        bubbleColor: 'white',
                        bubbleFontSize: 15,
                        bubbleContent: '1',
                        id: "Icon " + getRandomSmallKey()
                    } as ToolbarIconInfo;
                }
            }
    ]);
}
// --------------------------------------------------------------------------------------------
function getProps(iconInfo: ToolbarIconInfo, style: CSSProperties): ToolbarIconButtonProps {
    const iconProps: ToolbarIconButtonProps = {
        iconType: iconInfo.iconType,
        showBubble: iconInfo.showBubble,
        bubbleBackgroundColor: iconInfo.bubbleBackgroundColor,
        bubbleColor: iconInfo.bubbleColor,
        bubbleFontSize: iconInfo.bubbleFontSize,
        bubbleContent: iconInfo.bubbleContent,
        style: {
            ...style,
            ...(iconInfo.isClickable == false ? {
                cursor: 'unset',
                boxShadow: 'unset'
            } : {}),
            color: iconInfo.isClickable == false && !isDefined(iconInfo.iconColor)
                ? 'unset'
                : iconInfo.iconColor,
            ...iconInfo.style
        }
    };

    if (iconInfo.iconType == eIconType.fontAwesome)
        iconProps.className = iconInfo.icon;
    else {
        iconProps.src = iconInfo.icon;
        if (_.has(style, "height"))
            iconProps.height = style.height;
    }

    return iconProps;
};
//---------------------------------------------------------------------------------------------
function renderNestedItem(
    item: ToolbarIconInfo,
    i: number,
    hasNestedMenu: boolean,
    styleToolbarIcons?: any) {

    let { style, ...otherItemProps } = getProps(item, styleToolbarIcons);
    let labelStyles: CSSProperties = {
        margin: 12,
        fontSize: 14,
        width: '100%',
        color: item?.iconColor
    };

    let label = <div
        key={`label-${item.id}-${i}`}
        style={labelStyles}>
        {item?.label ?? item?.tooltip}
    </div>;

    let rightIcon = hasNestedMenu ? <div
        key={`right-icon-${item.id}-${i}`}
        style={{ position: 'unset' }}>
        <i className={item?.rightIcon ?? "fas fa-angle-right"}
            style={{
                fontSize: 16,
                lineHeight: '24px',
                textAlign: 'center',
                ...style,
                height: 24,
                width: 24
            }}>
        </i>
    </div> : null;

    let leftIcon = <ToolbarIconButton
        id="icons"
        key={`left-icon-${item.id}-${i}`}
        style={{
            fontSize: 16,
            lineHeight: '24px',
            textAlign: 'center',
            ...style,
            height: 24,
            width: 24
        }}
        inMenuList={true}
        {...otherItemProps}
    />;

    return <div
        style={{
            display: "flex",
            alignItems: "center",
            width: 'inherit'
        }}>
        {leftIcon}
        {label}
        {rightIcon}
    </div>;
}
//---------------------------------------------------------------------------------------------
function handleNestedItemClick(item: ToolbarIconInfo, onIconClick: (iconInfo: ToolbarIconInfo) => void) {
    if (item.isClickable != false) {
        onIconClick(item);
        if (!IsNullOrWhiteSpace(item.externalPageUrl))
            popupStaticWindow(item.externalPageUrl, true);
    }
}
//---------------------------------------------------------------------------------------------
function createNestedMenuItems(
    menuItem: ToolbarIconInfo,
    i: number,
    onIconClick: (iconInfo: ToolbarIconInfo) => void,
    closeFirstLevelDropdown: () => any,
    styleToolbarIcons: CSSProperties = {}) {

    return <Dropdown
        key={`dropdown-menu-item-${i}`}
        trigger={['hover', 'click']}
        placement={menuItem.nestedItemsPlacement ?? 'rightStart'}
        noCaret
        renderToggle={() => renderNestedItem(menuItem, i, true, styleToolbarIcons)}>
        {_.map(menuItem.nestedItems, (item, index) => {
            if (item.isSeparator)
                return <hr key={`separator-${item.id}-${index}`} />;

            let hasNestedMenu = _.isArray(item?.nestedItems) ? true : false;
            let onSelect = () => {
                if (!hasNestedMenu)
                    handleNestedItemClick(item, onIconClick);
                closeFirstLevelDropdown();
            }

            return <Dropdown.Item
                id="dropdown-item"
                onSelect={onSelect}
                eventKey={`inner-dropdown-item-${item.id}`}
                key={`inner-dropdown-item-${item.id}-${index}`}
            >
                {hasNestedMenu
                    ? createNestedMenuItems(item, index, onIconClick, closeFirstLevelDropdown, styleToolbarIcons)
                    : renderNestedItem(item, index, false, styleToolbarIcons)}
            </Dropdown.Item>
        })}
    </Dropdown>;
}
// --------------------------------------------------------------------------------------------
function renderNestedItems(
    item: ToolbarIconInfo,
    i: number,
    onIconClick: (iconInfo: ToolbarIconInfo) => void,
    styleToolbarIcons: CSSProperties = {},
    closeFirstLevelDropdown: () => any) {

    if (item.isSeparator)
        return <hr key={'separator-' + item.id + i} />;

    let hasNestedMenu = item?.nestedItems?.length > 0 ? true : false;
    let onSelect = undefined;
    if (!hasNestedMenu)
        onSelect = () => {
            handleNestedItemClick(item, onIconClick);
            closeFirstLevelDropdown();
        }

    // first level
    return <Dropdown.Item
        key={`outer-dropdown-item-${i}`}
        id="dropdown-item"
        onSelect={onSelect}
        eventKey={item.id}
        style={{ color: item?.iconColor }}>
        {hasNestedMenu
            ? createNestedMenuItems(item, i, onIconClick, closeFirstLevelDropdown, styleToolbarIcons)
            : renderNestedItem(item, i, false, styleToolbarIcons)}
    </Dropdown.Item>;
}
// --------------------------------------------------------------------------------------------
export function ToolbarIconInfo_ButtonRender(
    toolbarIcons: ToolbarIconInfo[],
    hiddenToolbarIcons: string[],
    selectedToolbarIcons: string[],
    styleToolbarIcons: CSSProperties = {},
    onIconClick: (iconInfo: ToolbarIconInfo) => void,
    renderFrom?: string,
    buttonPressed?: (element: any, data: any) => void,
    elementChanged?: (dataPath: string, data: any, prevData: any, formData: any) => void) {

    let fromCharts = renderFrom == 'charts';
    let bubbleStyle: CSSProperties = {
        height: fromCharts ? 12 : 14,
        width: fromCharts ? 12 : 16,
        top: fromCharts ? 4 : 10,
        right: 10
    };

    const icons = _(toolbarIcons)
        .filter(x => !_.includes(hiddenToolbarIcons, x.id))
        .map((iconInfo, index) => {
            let isSeparator = iconInfo.isSeparator;
            if (isSeparator)
                return [
                    <ToolbarIconButtonSeparator
                        key={`separator-${iconInfo.id}-${index}`}
                        style={{ backgroundColor: iconInfo.iconColor, ...styleToolbarIcons }} />
                ];

            // inner items
            let nestedItems = _.filter(iconInfo.nestedItems, icon =>
                icon != undefined && !_.includes(hiddenToolbarIcons, icon.id));
            let hasNestedItems = _.isArray(nestedItems) && nestedItems.length > 0;
            let isImageOrText = iconInfo.type == 'image' || iconInfo.type == 'text';

            // open/close inner
            let triggerRef: any = createRef();
            const closeMenu = () => {
                if (isDefined(triggerRef)) {
                    if (isDefined(triggerRef.current))
                        triggerRef.current.close();
                    else
                        triggerRef.close();
                }
            };
            const openMenu = () => {
                if (isDefined(triggerRef)) {
                    if (isDefined(triggerRef.current))
                        triggerRef.current.open();
                    else
                        triggerRef.open();
                }
            };

            // click callback
            let onClick = () => {
                if (iconInfo.isClickable != false) {
                    onIconClick(iconInfo);
                    if (!IsNullOrWhiteSpace(iconInfo.externalPageUrl))
                        popupStaticWindow(iconInfo.externalPageUrl, true);
                }
                // open manually (prevented from tippy)
                if (hasNestedItems && iconInfo.onIconClickDropdown)
                    openMenu();
            };

            let domNode: ReactNode = null;
            if (isImageOrText) {
                let style: CSSProperties = {
                    width: 'auto',
                    ...styleToolbarIcons,
                    ...(iconInfo.isClickable == false ? {
                        cursor: 'unset',
                        boxShadow: 'unset'
                    } : {}),
                    color: iconInfo.isClickable == false && !isDefined(iconInfo.iconColor)
                        ? 'unset'
                        : iconInfo.iconColor,
                    ...iconInfo.style
                };

                let className = 'toolbar-icon-text';
                if (_.includes(selectedToolbarIcons, iconInfo.id))
                    className += ' selected';

                if (iconInfo.type == 'image')
                    domNode = <img
                        key={`text-${iconInfo.id}-${index}`}
                        id={iconInfo.isClickable == false ? '' : 'icons'}
                        className={className}
                        style={style}
                        src={iconInfo.icon}
                        onClick={onClick}>
                    </img>;
                else if (iconInfo.type == 'text')
                    domNode = <div
                        key={`text-${iconInfo.id}-${index}`}
                        id='icons'
                        className={className}
                        style={style}
                        onClick={onClick}>
                        {iconInfo.label}
                    </div>;

                if (IsNullOrWhiteSpace(iconInfo.tooltip)
                    && !isDefined(iconInfo.advancedTooltip)
                    && !isDefined(iconInfo.advancedPopup)
                    && !hasNestedItems)
                    return [domNode];

                domNode = (
                    <AdvancedTippy
                        key={`tippy-${iconInfo.id}-${index}`}
                        placement='bottom'
                        tooltip={iconInfo.tooltip}
                        advancedTooltip={iconInfo.advancedTooltip}
                        advancedPopup={iconInfo.advancedPopup}
                        iconClicked={onClick}
                        buttonPressed={buttonPressed}
                        elementChanged={elementChanged}>
                        {domNode}
                    </AdvancedTippy>
                );
                if (!hasNestedItems)
                    return [domNode];
            } else {
                domNode = (
                    <ToolbarIconButton
                        key={`icon-${iconInfo.id}-${index}`}
                        isHighlighted={iconInfo.isHighlighted}
                        selected={_.includes(selectedToolbarIcons, iconInfo.id)}
                        tooltip={iconInfo.tooltip}
                        bubbleOtherStyles={bubbleStyle}
                        advancedTooltip={iconInfo.advancedTooltip}
                        advancedPopup={iconInfo.advancedPopup}
                        buttonPressed={buttonPressed}
                        elementChanged={elementChanged}
                        onClick={onClick}
                        {...getProps(iconInfo, styleToolbarIcons)} />
                );
            }

            const speaker = (props: any, ref: any) => {
                return (
                    <Popover
                        full
                        ref={ref}
                        arrow={false}
                        className={`${props?.className} rs-popover`}
                        style={{
                            left: props?.left,
                            top: props?.top,
                            border: 'none',
                            zIndex: 20000
                        }}>
                        <Dropdown.Menu className='yodiwo-toolbar-menu' style={{ backgroundColor: 'inherit', border: 'none' }}>
                            {_.map(nestedItems, (item, i) => {
                                if (item.nestedItems?.length > 0)
                                    item.nestedItems = _.filter(item.nestedItems, ni => !_.includes(hiddenToolbarIcons, ni?.id));
                                return renderNestedItems(item, i, onIconClick, styleToolbarIcons, closeMenu);
                            })}
                        </Dropdown.Menu>
                    </Popover>
                );
            };

            const whisper = (children: any) => {
                return (
                    <Whisper
                        ref={(ref: any) => triggerRef = ref}
                        key={`whisper-${iconInfo.id}-${index}`}
                        trigger='click'
                        placement='autoVerticalStart'
                        preventOverflow
                        speaker={speaker}>
                        {children}
                    </Whisper>
                );
            };

            if (hasNestedItems && iconInfo.onIconClickDropdown) {
                return [whisper(domNode)];
            } else {
                let nodes = [domNode];
                if (hasNestedItems && (iconInfo.onIconClickDropdown === false || iconInfo.onIconClickDropdown == null)) {
                    nodes.push(whisper(
                        <div style={{ position: 'relative' }}>
                            <i
                                id='icons'
                                key={`dropdown-toggle-${iconInfo.id}-${index}`}
                                className='highlighted toolbar-icon-button fas fa-caret-down'></i>
                        </div>
                    ));
                }
                return nodes;
            }
        })
        .flatMap()
        .value();

    return icons;
}